import React, { useEffect, useMemo, useState } from "react";
import {
    PaymentElement,
    useStripe,
    useElements,
} from "@stripe/react-stripe-js";
import { toast } from "react-toastify";
import Button from "../Button/Button";
import { useTranslations } from "next-intl";
import { sendMetaEvent } from "@/lib/tracking/metaEvent";
import WithdrawalConsent, {
    recordWithdrawalConsent,
} from "@/components/Checkout/WithdrawalConsent";

export default function CheckoutForm({
    onboarding = false,
    onPaymentSuccess = null,
    onSkip = null,
    billingAddress = null,
    subscriptionId = null,
    ...props
}) {
    const stripe = useStripe();
    const elements = useElements();
    const t = useTranslations();

    const [message, setMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    // Accès immédiat et renonciation au droit de rétractation : obligatoire
    // avant de payer (voir lib/withdrawalConsent.js).
    const [waiverChecked, setWaiverChecked] = useState(false);
    const [showWaiverError, setShowWaiverError] = useState(false);
    // "seti_" = SetupIntent (essai gratuit), "pi_" = PaymentIntent (paiement)
    const isTrialSetup = props.clientSecret?.startsWith("seti_");

    // Pays et code postal déjà saisis à l'étape précédente : Stripe les
    // préremplit au lieu de les redemander avec un pays deviné, parfois faux.
    const billingCountry = billingAddress?.country || null;
    const billingPostalCode = billingAddress?.postalCode || null;
    const paymentElementOptions = useMemo(
        () =>
            billingCountry
                ? {
                      defaultValues: {
                          billingDetails: {
                              address: {
                                  country: billingCountry,
                                  ...(billingPostalCode && {
                                      postal_code: billingPostalCode,
                                  }),
                              },
                          },
                      },
                  }
                : undefined,
        [billingCountry, billingPostalCode],
    );

    useEffect(() => {
        if (!stripe) {
            return;
        }

        const clientSecret = new URLSearchParams(window.location.search).get(
            "payment_intent_client_secret",
        );

        if (!clientSecret) {
            return;
        }

        stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
            // switch (paymentIntent.status) {
            //     case "succeeded":
            //         toast.success("Paiement effectué avec succès.");
            //         break;
            //     case "processing":
            //         toast.info("Paiement en cours.");
            //         break;
            //     case "requires_payment_method":
            //         toast.error(
            //             "Paiement refusé. Veuillez rééssayer avec un autre moyen de paiement.",
            //         );
            //         break;
            //     default:
            //         toast.error("Veuillez essayer à nouveau.");
            //         break;
            // }
        });
    }, [stripe]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        if (!waiverChecked) {
            setShowWaiverError(true);
            return;
        }

        setIsLoading(true);

        // La trace de l'accord part chez Stripe AVANT le paiement : le
        // webhook qui envoie l'e-mail de confirmation la relit.
        await recordWithdrawalConsent({
            clientSecret: props.clientSecret,
            subscriptionId,
        });

        let result = null;

        // Détection automatique basée sur le type de clientSecret
        // "seti_" = SetupIntent (trial/essai), "pi_" = PaymentIntent (paiement direct)
        const isSetupIntent = props.clientSecret?.startsWith("seti_");

        // Déterminer l'URL de redirection
        let redirectUrl;
        if (onPaymentSuccess) {
            redirectUrl = window.location.href;
        } else if (props.mentoringForAccelerator) {
            redirectUrl = `${process.env.NEXTAUTH_URL}/accelerators/${props.mentoringForAccelerator}/dashboard?subscription_started=1`;
        } else {
            // Essential = univers Reps : on dirige vers /reps, pas /courses.
            const isEssential =
                props.type === "essential" ||
                props.type === "essential-annually";
            const destination = isEssential ? "reps" : "courses";
            redirectUrl = `${process.env.NEXTAUTH_URL}/${destination}?subscription_started=1&subscription_type=${props.type}&subscription_price=${props.price}`;
        }

        if (isSetupIntent) {
            result = await stripe.confirmSetup({
                elements,
                confirmParams: { return_url: redirectUrl },
                redirect: "if_required",
            });
        } else if (props.mentoringForAccelerator) {
            result = await stripe.confirmPayment({
                elements,
                confirmParams: { return_url: redirectUrl },
                redirect: "if_required",
            });
        } else {
            result = await stripe.confirmPayment({
                elements,
                confirmParams: { return_url: redirectUrl },
                redirect: "if_required",
            });
        }

        setIsLoading(false);

        if (result.error) {
            // Gestion des erreurs
            toast.error(result.error.message);
        } else {
            // Paiement réussi
            const { paymentIntent, setupIntent } = result;
            const intent = paymentIntent || setupIntent;

            if (intent && intent.status === "succeeded") {
                toast.success(
                    t("CheckoutForm_payment_success") ||
                        "Paiement effectué avec succès !",
                );

                // Pixel du navigateur et API Conversions sous le même
                // identifiant d'événement : sans lui, un achat comptait double
                // dès que les deux envois aboutissaient.
                const fbEventName = isSetupIntent ? "StartTrial" : "Subscribe";
                sendMetaEvent(fbEventName);

                // Si un callback est fourni, l'appeler au lieu de rediriger
                if (onPaymentSuccess) {
                    onPaymentSuccess({
                        paymentIntent: paymentIntent,
                        setupIntent: setupIntent,
                        trial: props.trial,
                        mentoringForAccelerator: props.mentoringForAccelerator,
                        pro: props.pro,
                        price: props.price,
                    });
                } else {
                    // Redirection manuelle après tracking
                    window.location.href = redirectUrl;
                }
            } else if (intent && intent.status === "processing") {
                toast.info("Paiement en cours de traitement...");
            }
        }
    };

    return (
        <form
            id="payment-form"
            onSubmit={handleSubmit}
            width="100%"
            className="mt-4"
        >
            <PaymentElement
                id="payment-element"
                options={paymentElementOptions}
            />
            <WithdrawalConsent
                className="mt-5"
                checked={waiverChecked}
                onChange={(checked) => {
                    setWaiverChecked(checked);
                    if (checked) setShowWaiverError(false);
                }}
                showError={showWaiverError}
            />
            <div
                className={`mt-5 flex ${
                    onboarding && onSkip ? "justify-between" : "justify-end"
                } items-center gap-4`}
            >
                {onboarding && onSkip && (
                    <Button
                        type="duo-secondary"
                        onClick={onSkip}
                        disabled={isLoading}
                    >
                        {t("onboarding_skip", { default: "Ignorer" })}
                    </Button>
                )}
                <Button
                    type={onboarding ? "duo" : "confidence"}
                    block={props.blockButton}
                    className={props.blockButton ? "w-full" : ""}
                    disabled={isLoading || !stripe || !elements}
                    id="submit"
                    isLoading={isLoading}
                    progress
                >
                    <span id="button-text">
                        <div className="flex items-center gap-3">
                            {isTrialSetup
                                ? t("CheckoutForm_startTrial")
                                : props.payLabel || t("CheckoutForm_pay")}
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path
                                    fill="none"
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="1.5"
                                    d="M4 12h16m0 0l-6-6m6 6l-6 6"
                                ></path>
                            </svg>
                        </div>
                    </span>
                </Button>
            </div>
            {/* Show any error or success messages */}
            {message && <div id="payment-message">{message}</div>}
        </form>
    );
}
